import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Button, FormControl, Stack, Modal, TextField, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../network/ApiClient';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { md: 500, xs: '100%' },
  bgcolor: 'background.paper',
  boxShadow: 24,
  maxHeight: '90vh',
  overflowY: 'auto',
  p: { md: 4, xs: 2 },
};

const vehicleValidationSchema = Yup.object({
  name: Yup.string().required('Vehicle name is required'),
  type: Yup.string().required('Vehicle type is required'),
  number: Yup.string().required('Vehicle number is required'),
  capacity: Yup.number()
    .required('Capacity is required')
    .positive('Capacity must be a positive number')
    .integer('Capacity must be an integer'),
  status: Yup.string().required('Status is required'),
  costperkm: Yup.number().required('Cost per kilometer is required').positive('Cost must be a positive number'),
});

const CreateVehicleForm = ({ onCreateCallback, modalShow, setModalShow, edit, vehicle }) => {
  // Merchant options. The dropdown is only shown when the caller belongs to more
  // than one merchant; a single-merchant caller has it auto-assigned. Merchant
  // is only chosen on create — the backend forbids changing it on update.
  const [optionState, setOptionState] = useState({ data: [] });

  const handleClose = () => {
    setModalShow(false);
    formik.resetForm();
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      type: '',
      number: '',
      capacity: '',
      status: 'Available',
      costperkm: '',
      organizergroup: null,
    },
    enableReinitialize: true,
    validationSchema: edit
      ? vehicleValidationSchema
      : vehicleValidationSchema.concat(Yup.object({ organizergroup: Yup.object().nullable().required('Merchant is required') })),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        if (edit && vehicle?.id) {
          const { organizergroup, ...rest } = values; // merchant can't change on edit
          await api.put(`/vehicle/${vehicle.id}`, rest);
        } else {
          await api.post('/vehicle', { ...values, organizergroup: values.organizergroup?.id });
        }
        onCreateCallback();
        handleClose();
        resetForm();
      } catch (error) {
        console.error('Error creating/updating vehicle:', error);
      }
      setSubmitting(false);
    },
  });

  const loadOrganizerGroups = async () => {
    try {
      const res = await api.get('organizergroup', { params: { simple: 'ok' } });
      const groups = res.data?.organizer || [];
      setOptionState({ data: groups });
      // Single-merchant users don't see the dropdown, so auto-assign it.
      if (!edit && groups.length === 1 && !formik.values.organizergroup) {
        formik.setFieldValue('organizergroup', groups[0]);
      }
    } catch (e) {
      console.error('Unable to load merchants', e);
    }
  };

  useEffect(() => {
    loadOrganizerGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (edit && vehicle) {
      formik.setValues({ organizergroup: null, ...vehicle });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, vehicle]);

  const renderTextField = (name, label, type = 'text', select = false, options = []) => (
    <TextField
      fullWidth
      name={name}
      label={label}
      type={type}
      select={select}
      onChange={formik.handleChange}
      value={formik.values[name]}
      variant="outlined"
      error={formik.touched[name] && Boolean(formik.errors[name])}
      helperText={formik.touched[name] ? formik.errors[name] : ''}
      InputLabelProps={{ shrink: true }}
    >
      {select &&
        options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
    </TextField>
  );

  return (
    <Modal
      open={modalShow}
      onClose={handleClose}
      aria-labelledby="create-vehicle-modal"
      aria-describedby="create-vehicle-form"
    >
      <Box sx={style}>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={3}>
            <h4 className="card-title mb-0">{edit ? 'Edit Vehicle' : 'Create Vehicle'}</h4>
            {renderTextField('name', 'Name')}
            {renderTextField('type', 'Type')}
            {renderTextField('number', 'Number')}
            {renderTextField('capacity', 'Capacity', 'number')}
            {renderTextField('status', 'Status', undefined, true, [
              { value: 'Available', label: 'Available' },
              { value: 'Unavailable', label: 'Unavailable' },
            ])}
            {renderTextField('costperkm', 'Cost per Kilometer', 'number')}

            {/* Merchant can't be changed after creation (the backend forbids it);
                show it read-only so multi-merchant users can see the owner. */}
            {edit && optionState.data.length > 1 ? (
              <TextField
                fullWidth
                label="Merchant"
                value={
                  optionState.data.find((o) => String(o.id) === String(vehicle?.organizergroup))?.name ||
                  vehicle?.organizergroup ||
                  '—'
                }
                variant="outlined"
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                disabled
              />
            ) : null}

            {!edit && optionState.data.length > 1 ? (
              <FormControl error={Boolean(formik.errors.organizergroup && formik.touched.organizergroup)} fullWidth>
                <Autocomplete
                  options={optionState.data}
                  onChange={(event, value) => formik.setFieldValue('organizergroup', value)}
                  getOptionLabel={(option) => `${option.name}`}
                  value={formik.values.organizergroup}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Merchant"
                      variant="outlined"
                      error={Boolean(formik.errors.organizergroup && formik.touched.organizergroup)}
                      helperText={formik.errors.organizergroup && formik.touched.organizergroup ? formik.errors.organizergroup : ''}
                    />
                  )}
                />
              </FormControl>
            ) : null}

            <Button fullWidth type="submit" variant="contained" disabled={formik.isSubmitting}>
              {formik.isSubmitting ? 'Submitting...' : edit ? 'Update Vehicle' : 'Create Vehicle'}
            </Button>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
};

export default CreateVehicleForm;
